import {useEffect, useMemo, useState} from 'react';
import {useDebounce} from '@netx/core-components/hooks/useDebounce';

export type UseFilterItemsParams<T = unknown> = {
	items: T[];
	filterFn: (filterStr: string, items: T[]) => T[];
	filter?: string | null;
};

/**
 * Client side filtering of an array of items
 */
export const useFilterItems = <T = unknown>({
	filter,
	items,
	filterFn,
}: UseFilterItemsParams<T>) => {
	const [_filter, setFilter] = useState(filter || '');
	const [debouncedFilter] = useDebounce(_filter, 200);

	useEffect(() => {
		setFilter(filter || '');
	}, [filter]);

	const filtered = useMemo(() => {
		if (debouncedFilter == null || debouncedFilter === '') {
			return {
				filteredItems: items,
				filtered: false,
			};
		}
		return {
			filteredItems: filterFn(debouncedFilter.toLowerCase(), items),
			filtered: true,
		};
	}, [debouncedFilter, filterFn, items]);

	return {
		...filtered,
		filter: _filter,
		setFilter,
	};
};

export default useFilterItems;
